<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { useHeaderStore } from '@/stores/header'
import { useMediaQueryStore } from '@/stores/mediaQuery'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'ToastMessageForLocalData',

  setup() {
    // constants
    const DEFAULT_DURATION = 8.2

    // store
    const headerStore = useHeaderStore()
    const mediaQueryStore = useMediaQueryStore()

    const items: Ref<ToastItem[]> = ref([])

    /**
     * トーストの表示位置
     *
     * @description ヘッダー表示時
     *                SP 表示時 = 画面上から 64px
     *                PC 表示時 = 画面上から 96px
     *
     *              ヘッダー非表示時
     *                SP・PC = 画面上から 12px 固定
     */
    const toastPosition = computed<string>(() => {
      return !headerStore.context.isShow
        ? '12px'
        : mediaQueryStore.context.isUnderPhablet
          ? '64px'
          : '96px'
    })

    /**
     * トーストアイテムの初期化が完了しているかどうか
     * @description items 配列のすべてのアイテムに id と message が定義されている
     */
    const isInitialized = computed<boolean>(() => {
      return items.value.every(v => !!v.id && !!v.message)
    })

    watch(() => items.value.length, (newValue, oldValue) => {
      if (newValue > oldValue) {
        initToastItem()
      }
    })

    const initToastItem = () => {
      items.value = items.value.filter(v => v.message).map(v => {
        return {
          id: v.id || `toast-${new Date().getTime()}`,
          messageType: v.messageType || 'notice',
          messageTitle: v.messageTitle || '',
          message: v.message || '',
          duration: v.duration || DEFAULT_DURATION,
        }
      })
    }

    /**
     * 新規トースト情報を追加する
     *
     * @param item セット対象のトーストデータ
     *
     * @description 呼び出し元の親コンポーネントからメソッドをつつく
     * @see https://qiita.com/mml/items/2be6b8df94d932e9a047
     */
    const setToastItem = (item: ToastItem) => {
      items.value.push(item)
    }

    const removeToastItem = (id: string) => {
      const index = items.value.findIndex(v => v.id === id)

      if (index !== -1) {
        items.value.splice(index, 1)
      }
    }

    const leave = (toastItemEl: Element) => {
      const toastId = toastItemEl.getAttribute('data-toast-id') || undefined
      if (toastId) removeToastItem(toastId)
    }

    const close = (id: string | undefined) => {
      if (id) removeToastItem(id)
    }

    /**
     * トーストクリア処理
     */
    const clearToast = async () => {
      items.value = []
    }

    return {
      // data
      items,

      // computed
      toastPosition,
      isInitialized,

      // methods
      setToastItem,
      close,
      leave,
      clearToast,
    }
  }
})
</script>

<template>
  <transition-group
    v-if="items.length && isInitialized"
    appear
    class="l-rows__toastMessage"
    tag="div"
    name="is-toast__slide"
    :style="{ top: toastPosition }"
    @after-enter="leave($event)">
    <div
      v-for="item in items"
      :key="item.id"
      :data-toast-id="item.id"
      class="l-rows__toastMessage--row"
      :style="{
        animationDuration: `${item.duration}s`,
      }">
      <div
        :class="[
          'l-unit__toastMessage',
          `is-${item.messageType}`,
        ]">
        <div
          v-if="item.messageTitle"
          class="l-unit__toastMessage--header">
          <div
            :class="[
              'o-label__toastMessageTitle',
              `is-${item.messageType}`,
            ]">
            <p class="o-label__toastMessageTitle--item">
              {{ item.messageTitle }}
            </p>
          <!-- /.o-label__toastMessageTitle --></div>
        <!-- /.l-unit__toastMessage header --></div>

        <div class="l-unit__toastMessage--description">
          <div
            :class="[
              'o-label__toastMessageDescription',
              `is-${item.messageType}`,
            ]"
            :data-has-icon="!item.messageTitle"
            :style="{
              top: toastPosition,
            }">
            <p
              class="o-label__toastMessageDescription--item"
              v-html="item.message" />
          <!-- /.o-label__toastMessageDescription --></div>
        <!-- /.l-unit__toastMessage description --></div>

        <div class="l-unit__toastMessage--action is-close">
          <div class="o-action__close">
            <button
              class="o-action__close--item"
              @click.prevent="close(item.id)">
              閉じる
            </button>
          <!-- /.o-action__close --></div>
        <!-- /.l-unit__toastMessage action --></div>
      <!-- /.l-unit__toastMessage --></div>
    <!-- /.l-rows__toastMessage row --></div>
  <!-- /.l-rows__toastMessage --></transition-group>
</template>

<style lang="sass" scoped>
$font-normal-px: 16
$icon-size-em: 1
$icon-margin-size-em: 0.4
$close-button-size: 0.75em

// layout
.l-rows__toastMessage
  $root: &
  max-width: #{$global-contents-width-pc}px
  display: flex
  flex-direction: column-reverse
  position: fixed
  top: 96px
  right: 0
  padding: 0 10px
  z-index: 21
  +transition($duration: $transition-long)

  & &--row
    display: grid
    grid-template-rows: 0fr
    transform: translateX(100%)
    margin-bottom: 1em
    opacity: 0
    visibility: hidden

    &:first-child
      margin-bottom: 0

  +breakpoint
    top: 16px
    padding: 0 12px

.l-unit__toastMessage
  $root: &
  $close-button-margin: 1em
  overflow: hidden
  position: relative
  padding: 1em
  padding-right: $close-button-size + $close-button-margin + 1em
  border-radius: $border-radius
  box-shadow: $drop-bottom-shadow

  & &--description
    margin-top: 0.4em

    &:first-child
      margin-top: 0

  & &--action
    &.is-close
      position: absolute
      top: 50%
      right: $close-button-margin
      transform: translateY(-50%)

  &.is-success
    background-color: rgba($bg-success, 1)
    border: 1px solid $border-success

  &.is-notice
    background-color: rgba($bg-notice, 1)
    border: 1px solid $border-notice

  &.is-alert
    background-color: rgba($bg-alert, 1)
    border: 1px solid $border-alert

  +breakpoint
    $close-button-margin: 0.6em
    padding: 0.5em 0.7em
    padding-right: $close-button-size + $close-button-margin + 0.7em

    #{$root}--action
      &.is-close
        right: $close-button-margin

// object
.o-label__toastMessageTitle
  $root: &

  & &--item
    font-weight: bold
    padding-left: #{$icon-size-em + $icon-margin-size-em}em

    &::before
      content: ""
      width: #{$icon-size-em + $icon-margin-size-em}em
      height: #{$icon-size-em}em
      display: inline-block
      text-align: left
      vertical-align: middle
      margin:
        top: -0.2em
        left: -#{$icon-size-em + $icon-margin-size-em}em
      background:
        repeat: no-repeat
        position: center left
        size: contain

  &.is-success
    #{$root}--item
      &::before
        background-image: url(@/images/icon/toast/icon-success.svg)

  &.is-notice
    #{$root}--item
      &::before
        background-image: url(@/images/icon/toast/icon-notice.svg)

  &.is-alert
    #{$root}--item
      &::before
        background-image: url(@/images/icon/toast/icon-alert.svg)

  +breakpoint
    #{$root}--item
      font-size: $font-x-small

.o-label__toastMessageDescription
  $root: &
  $heading-font-size-px: $font-normal-px

  & &--item
    $padding-px: ($heading-font-size-px * $icon-size-em) + ($heading-font-size-px * $icon-margin-size-em)
    font-size: $font-small
    padding-left: changeSizePx2Em($padding-px, $font-small-px)

  &[data-has-icon="true"]
    #{$root}--item
      $padding-px: ($font-small-px * $icon-size-em) + ($font-small-px * $icon-margin-size-em)
      padding-left: changeSizePx2Em($padding-px, $font-small-px)

      &::before
        content: ""
        width: #{$icon-size-em + $icon-margin-size-em}em
        height: #{$icon-size-em}em
        display: inline-block
        text-align: left
        vertical-align: middle
        margin:
          top: -0.2em
          left: -#{$icon-size-em + $icon-margin-size-em}em
        background:
          repeat: no-repeat
          position: center left
          size: contain

  &.is-success
    #{$root}--item
      &::before
        background-image: url(@/images/icon/toast/icon-success.svg)

  &.is-notice
    #{$root}--item
      &::before
        background-image: url(@/images/icon/toast/icon-notice.svg)

  &.is-alert
    #{$root}--item
      &::before
        background-image: url(@/images/icon/toast/icon-alert.svg)

  +breakpoint
    #{$root}--item
      $heading-font-size-px: $font-x-small-px
      $padding-px: ($heading-font-size-px * $icon-size-em) + ($heading-font-size-px * $icon-margin-size-em)
      font-size: $font-xx-small
      padding-left: changeSizePx2Em($padding-px, $font-xx-small-px)

.o-action__close
  & &--item
    +closeButtonStyle($icon-size: $close-button-size, $border-color: $border-x-dark-gray, $border-width: 0.1em)
    +transition($duration: $transition-middle)
    width: $close-button-size
    height: $close-button-size
    border-radius: 50%

    &::before,
    &::after
      +transition($duration: $transition-middle)

    +mouseover
      background-color: rgba($bg-black, 0.05)

      &::before,
      &::after
        height: 0.12em

// vue transition
.is-toast__slide
  &-enter-active
    animation:
      name: toastMessageSlide
      fill-mode: forwards

@keyframes toastMessageSlide
  0%
    grid-template-rows: 0fr
    transform: translateX(100%)
    opacity: 0
    visibility: hidden

  5%
    grid-template-rows: 1fr
    transform: translateX(0)
    opacity: 1
    visibility: visible

  95%
    grid-template-rows: 1fr
    transform: translateX(0)
    opacity: 1
    visibility: visible

  100%
    grid-template-rows: 0fr
    transform: translateX(100%)
    opacity: 0
    visibility: hidden
</style>
